import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock, Download, User, Calendar, Briefcase, Filter, Trash2, Plus, Pencil, CheckCircle2, XCircle } from "lucide-react";

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths, subWeeks } from "date-fns";
import { nb } from "date-fns/locale";
import TimeTracker from "@/components/time/TimeTracker";
import TimeEntryForm from "@/components/time/TimeEntryForm";
import { toast } from "sonner";

export default function TimeTracking() {
  const [periodFilter, setPeriodFilter] = useState("month");
  const [userFilter, setUserFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: timeEntries = [], isLoading } = useQuery({
    queryKey: ["timeEntries"],
    queryFn: () => base44.entities.TimeEntry.list("-start_time"),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id) => base44.entities.TimeEntry.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
    },
  });

  const createEntryMutation = useMutation({
    mutationFn: (data) => base44.entities.TimeEntry.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      setShowForm(false);
      setEditingEntry(null);
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TimeEntry.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      setShowForm(false);
      setEditingEntry(null);
    },
  });

  const approveEntryMutation = useMutation({
    mutationFn: ({ id, entry }) => base44.entities.TimeEntry.update(id, {
      ...entry,
      approved: true,
      approved_by: user?.email,
      approved_by_name: getDisplayName(user),
      approved_date: new Date().toISOString(),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      toast.success("Tidsregistrering godkjent");
    },
  });

  const unapproveEntryMutation = useMutation({
    mutationFn: ({ id, entry }) => base44.entities.TimeEntry.update(id, {
      ...entry,
      approved: false,
      approved_by: null,
      approved_by_name: null,
      approved_date: null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      toast.success("Godkjenning fjernet");
    },
  });

  const handleEntrySubmit = (data) => {
    if (editingEntry) {
      updateEntryMutation.mutate({ id: editingEntry.id, data });
    } else {
      createEntryMutation.mutate(data);
    }
  };

  // Filter by period
  const filterByPeriod = (entry) => {
    if (!entry.start_time) return false;
    const entryDate = parseISO(entry.start_time);
    const now = new Date();

    switch (periodFilter) {
      case "today":
        return format(entryDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");
      case "week":
        return entryDate >= startOfWeek(now, { locale: nb }) && entryDate <= endOfWeek(now, { locale: nb });
      case "last_week": {
        const lastWeek = subWeeks(now, 1);
        return entryDate >= startOfWeek(lastWeek, { locale: nb }) && entryDate <= endOfWeek(lastWeek, { locale: nb });
      }
      case "month":
        return entryDate >= startOfMonth(now) && entryDate <= endOfMonth(now);
      case "last_month": {
        const lastMonth = subMonths(now, 1);
        return entryDate >= startOfMonth(lastMonth) && entryDate <= endOfMonth(lastMonth);
      }
      case "all":
        return true;
      default:
        return true;
    }
  };

  const filteredEntries = timeEntries
    .filter(entry => !entry.is_running)
    .filter(filterByPeriod)
    .filter(entry => userFilter === "all" || entry.user_email === userFilter)
    .filter(entry => projectFilter === "all" || entry.project_id === projectFilter)
    .filter(entry => {
      if (approvalFilter === "all") return true;
      if (approvalFilter === "approved") return entry.approved === true;
      if (approvalFilter === "pending") return entry.approved !== true;
      return true;
    });

  // Calculate totals
  const totalMinutes = filteredEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);
  const approvedMinutes = filteredEntries.filter(e => e.approved).reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
  const approvedHours = (approvedMinutes / 60).toFixed(1);
  const pendingMinutes = filteredEntries.filter(e => !e.approved).reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
  const pendingHours = (pendingMinutes / 60).toFixed(1);

  // Group by project
  const timeByProject = filteredEntries.reduce((acc, entry) => {
    const key = entry.project_name || "Ingen prosjekt";
    if (!acc[key]) {
      acc[key] = { minutes: 0, count: 0 };
    }
    acc[key].minutes += entry.duration_minutes || 0;
    acc[key].count += 1;
    return acc;
  }, {});

  // Group by user — look up current display name from users list
  const timeByUser = filteredEntries.reduce((acc, entry) => {
    const matchedUser = users.find(u => u.email === entry.user_email);
    const key = matchedUser ? getDisplayName(matchedUser) : (entry.user_name || entry.user_email || "Ukjent");
    if (!acc[key]) {
      acc[key] = { minutes: 0, count: 0 };
    }
    acc[key].minutes += entry.duration_minutes || 0;
    acc[key].count += 1;
    return acc;
  }, {});

  const exportToCSV = () => {
    const headers = ["Bruker", "Prosjekt", "Oppgave", "Start", "Slutt", "Varighet (timer)", "Beskrivelse", "Relatert til", "Fakturerbar", "Godkjent", "Godkjent av"];
    const rows = filteredEntries.map(entry => [
      entry.user_name || "",
      entry.project_name || "",
      entry.task_title || "",
      entry.start_time ? format(parseISO(entry.start_time), "dd.MM.yyyy HH:mm") : "",
      entry.end_time ? format(parseISO(entry.end_time), "dd.MM.yyyy HH:mm") : "",
      ((entry.duration_minutes || 0) / 60).toFixed(2),
      entry.description || "",
      entry.related_to_name || "",
      entry.billable ? "Ja" : "Nei",
      entry.approved ? "Ja" : "Nei",
      entry.approved_by_name || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `timeliste_${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}t ${mins}m`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Tidsregistrering</h1>
            <div className="flex items-center gap-4 mt-1 text-sm">
              <span className="text-slate-500">{totalHours}t totalt</span>
              <span className="text-emerald-600 font-medium">{approvedHours}t godkjent</span>
              <span className="text-amber-600 font-medium">{pendingHours}t avventer</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowForm(true)} variant="outline" className="border-slate-200">
              <Plus className="w-4 h-4 mr-2" /> Registrer Tid
            </Button>
            <Button onClick={exportToCSV} className="bg-slate-900 hover:bg-slate-800">
              <Download className="w-4 h-4 mr-2" /> Eksporter CSV
            </Button>
          </div>
        </div>

        {/* Timer */}
        <div className="mb-8">
          <TimeTracker />
        </div>

        {/* Filters */}
        <Card className="p-6 bg-white border-0 shadow-sm mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Filtre</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">I dag</SelectItem>
                <SelectItem value="week">Denne uken</SelectItem>
                <SelectItem value="last_week">Forrige uke</SelectItem>
                <SelectItem value="month">Denne måneden</SelectItem>
                <SelectItem value="last_month">Forrige måned</SelectItem>
                <SelectItem value="all">Alle perioder</SelectItem>
              </SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Alle brukere" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle brukere</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.email}>{getDisplayName(u)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Alle prosjekter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle prosjekter</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={approvalFilter} onValueChange={setApprovalFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Godkjenningsstatus" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statuser</SelectItem>
                <SelectItem value="approved">Godkjent</SelectItem>
                <SelectItem value="pending">Avventer godkjenning</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-5 h-5 text-zinc-900" />
              <h3 className="font-semibold text-slate-900">Per Prosjekt</h3>
            </div>
            <div className="space-y-3">
              {Object.entries(timeByProject)
                .sort(([, a], [, b]) => b.minutes - a.minutes)
                .slice(0, 5)
                .map(([project, data]) => (
                  <div key={project} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{project}</p>
                      <p className="text-xs text-slate-500">{data.count} registreringer</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-zinc-900">{(data.minutes / 60).toFixed(1)}t</p>
                    </div>
                  </div>
                ))}
            </div>
          </Card>

          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-zinc-900" />
              <h3 className="font-semibold text-slate-900">Per Bruker</h3>
            </div>
            <div className="space-y-3">
              {Object.entries(timeByUser)
                .sort(([, a], [, b]) => b.minutes - a.minutes)
                .map(([userName, data]) => (
                  <div key={userName} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{userName}</p>
                      <p className="text-xs text-slate-500">{data.count} registreringer</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-zinc-900">{(data.minutes / 60).toFixed(1)}t</p>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* Time Entries List */}
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Registreringer</h3>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Ingen tidsregistreringer funnet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEntries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-medium text-slate-900">{(() => { const u = users.find(x => x.email === entry.user_email); return u ? getDisplayName(u) : (entry.user_name || entry.user_email); })()}</p>
                      {entry.project_name && (
                        <Badge variant="outline" className="text-xs">
                          {entry.project_name}
                        </Badge>
                      )}
                      {entry.task_title && (
                        <Badge variant="outline" className="text-xs">
                          {entry.task_title}
                        </Badge>
                      )}
                      {entry.approved ? (
                        <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">
                          Godkjent
                        </Badge>
                      ) : (
                        <Badge className="text-xs bg-amber-100 text-amber-700 border-0">
                          Avventer
                        </Badge>
                      )}
                      {!entry.billable && (
                        <Badge variant="outline" className="text-xs text-slate-500">
                          Ikke fakturerbar
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {entry.start_time && format(parseISO(entry.start_time), "dd.MM.yyyy HH:mm", { locale: nb })}
                      </span>
                      {entry.related_to_name && (
                        <span>→ {entry.related_to_name}</span>
                      )}
                      {entry.approved && entry.approved_by_name && (
                        <span className="text-emerald-600">
                          Godkjent av {entry.approved_by_name}
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="text-sm text-slate-600 mt-1">{entry.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="font-semibold text-zinc-900">
                        {formatDuration(entry.duration_minutes || 0)}
                      </p>
                    </div>
                    {user?.role === "admin" && (
                      entry.approved ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => unapproveEntryMutation.mutate({ id: entry.id, entry })}
                          title="Fjern godkjenning"
                        >
                          <XCircle className="w-4 h-4 text-emerald-600 hover:text-slate-600" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => approveEntryMutation.mutate({ id: entry.id, entry })}
                          title="Godkjenn tidsregistrering"
                        >
                          <CheckCircle2 className="w-4 h-4 text-slate-400 hover:text-emerald-600" />
                        </Button>
                      )
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingEntry(entry);
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="w-4 h-4 text-slate-400 hover:text-zinc-900" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => deleteEntryMutation.mutate(entry.id)}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Time Entry Form */}
        {showForm && (
          <TimeEntryForm
            entry={editingEntry}
            onSubmit={handleEntrySubmit}
            onClose={() => {
              setShowForm(false);
              setEditingEntry(null);
            }}
            isLoading={createEntryMutation.isPending || updateEntryMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}